import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { applierGetJobs } from "../../api/jobs";
import JobCard from "../../components/ui/ui/JobCard";
import FilterPanel from "../../components/ui/ui/FilterPanel";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import Loader from "../../components/ui/Loader";
import { glassPanel } from "../../utils/glassStyles";

const JobSearch = () => {
  const [searchParams] = useSearchParams();
  const companyParam = searchParams.get("company");

  const [jobs, setJobs] = useState([]);
  const [filters, setFilters] = useState(companyParam ? { company: companyParam } : {});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const fetchJobs = async (f = {}, page = 1) => {
    try {
      setLoading(true);
      setError("");
      const params = { ...f, page, limit: 12 };
      const res = await applierGetJobs(params);
      setJobs(res.data.jobs || []);
      setPagination(res.data.pagination || {});
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
      setJobs([]);
      setError("Failed to load jobs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs(filters, currentPage);
  }, [currentPage, filters]);

  useEffect(() => {
    if (companyParam) {
      setFilters((prev) => ({ ...prev, company: companyParam }));
      setCurrentPage(1);
    }
  }, [companyParam]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    fetchJobs(newFilters, 1);
    setFilterDialogOpen(false);
  };

  const handleSearch = () => {
    handleFilterChange({ ...filters, q: searchQuery });
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="applier-page">
      <div className="applier-page-inner">
        <div className="mb-5 sm:mb-8">
          <h1 className="font-display text-xl sm:text-2xl md:text-3xl font-bold mb-1.5 tracking-tight leading-tight">
            Explore Jobs on{" "}
            <span className="text-brand-gradient">
              Shebeka
            </span>
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Discover opportunities that match your skills across Ethiopia
          </p>
        </div>

        <div className={`${glassPanel} p-3 sm:p-4 rounded-2xl mb-4 sm:mb-6 space-y-2 sm:space-y-0 sm:flex sm:gap-2`}>
          <Input
            type="text"
            placeholder="Search by title, company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            className="h-11 rounded-xl bg-background/60 dark:bg-background/40 border-border/60 w-full sm:flex-1"
          />
          <Button onClick={handleSearch} className="h-11 w-full sm:w-auto gap-2 px-5 rounded-xl shrink-0">
            <Search className="w-4 h-4" />
            Search
          </Button>
        </div>

        <div className="lg:hidden mb-4">
          <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="w-full h-11 gap-2 rounded-xl bg-background/60 dark:bg-background/40 border-border/60 backdrop-blur-sm font-medium"
              >
                <Filter className="w-4 h-4 text-primary" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-1.5">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[85dvh] overflow-y-auto rounded-2xl p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle className="font-display text-lg">Filters</DialogTitle>
              </DialogHeader>
              <FilterPanel onFilter={handleFilterChange} />
            </DialogContent>
          </Dialog>
        </div>

        <div className="lg:grid lg:grid-cols-4 lg:gap-6">
          <div className="hidden lg:block lg:col-span-1">
            <FilterPanel onFilter={handleFilterChange} />
          </div>

          <div className="lg:col-span-3 min-w-0">
            {loading ? (
              <Loader />
            ) : error ? (
              <div className="text-center py-12 sm:py-16">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-3">
                  <X className="w-6 h-6 text-destructive" />
                </div>
                <p className="text-sm sm:text-base font-semibold mb-3">{error}</p>
                <Button onClick={() => fetchJobs(filters, currentPage)} className="rounded-xl h-10">
                  Try Again
                </Button>
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-12 sm:py-16">
                <Search className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm sm:text-base font-semibold mb-1">No jobs found</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Try adjusting your filters</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {jobs.map((job) => (
                    <JobCard key={job.id} job={job} />
                  ))}
                </div>

                {pagination.pages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-6 sm:mt-8">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => p - 1)}
                      disabled={currentPage === 1}
                      className="h-9 rounded-xl px-3"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-xs sm:text-sm text-muted-foreground tabular-nums">
                      {currentPage} / {pagination.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={currentPage === pagination.pages}
                      className="h-9 rounded-xl px-3"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSearch;
